import { z } from 'zod';
import { Email, IsoDateTime, PageQuery, Uuid, page, patchOf } from './common.js';
/** Module `events-mgmt` (docs/modules/events-mgmt.md, spec 2.5). Owner: task S27. "Events" here are open days, courses and the like. */
export const SiteEventStatus = z.enum(['draft', 'published', 'cancelled']);
export const SiteEvent = z.object({
    id: Uuid,
    siteId: Uuid,
    name: z.string(),
    description: z.string(),
    startsAt: IsoDateTime,
    endsAt: IsoDateTime,
    status: SiteEventStatus,
    /** Groups whose sign-ins at this site during the event count as attendance. Event-only groups show on the kiosk only in the window. */
    groupIds: z.array(Uuid),
    capacity: z.number().int().nullable(),
    counts: z.object({ expected: z.number().int(), arrived: z.number().int(), walkIns: z.number().int() }),
    createdAt: IsoDateTime,
});
export const SiteEventCreate = z.object({
    siteId: Uuid,
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(2000).default(''),
    startsAt: IsoDateTime,
    endsAt: IsoDateTime,
    groupIds: z.array(Uuid).min(1).max(20),
    capacity: z.number().int().min(1).nullable().default(null),
});
export const SiteEventUpdate = patchOf(SiteEventCreate.omit({ siteId: true })).extend({ status: SiteEventStatus.optional() });
export const SiteEventListQuery = PageQuery.extend({ siteId: Uuid.optional(), when: z.enum(['upcoming', 'past', 'all']).default('upcoming') });
export const SiteEventList = page(SiteEvent);
export const AttendeeStatus = z.enum(['expected', 'arrived', 'no_show', 'cancelled']);
export const EventAttendee = z.object({
    id: Uuid,
    eventId: Uuid,
    name: z.string(),
    email: z.string().nullable(),
    company: z.string(),
    memberId: Uuid.nullable(),
    preRegistrationId: Uuid.nullable(),
    visitId: Uuid.nullable(),
    status: AttendeeStatus,
    walkIn: z.boolean(),
    arrivedAt: IsoDateTime.nullable(),
});
export const EventAttendeeCreate = z.object({
    name: z.string().trim().min(1).max(120),
    email: Email.nullable().default(null),
    company: z.string().trim().max(120).default(''),
    memberId: Uuid.nullable().default(null),
});
/** POST /events-mgmt/events/:id/attendees/bulk (pasted list or CSV rows already parsed by the portal). */
export const EventAttendeesBulk = z.object({ attendees: z.array(EventAttendeeCreate).min(1).max(2000), preRegister: z.boolean().default(false) });
export const EventAttendeesBulkResult = z.object({ added: z.number().int(), skippedDuplicates: z.number().int(), preRegistered: z.number().int() });
export const EventAttendeeList = page(EventAttendee);
/** Kiosk config section `events-mgmt`: event windows so event-only groups show only during their event. */
export const KioskEventsSection = z.object({
    events: z.array(z.object({ id: Uuid, name: z.string(), groupIds: z.array(Uuid), showFrom: IsoDateTime, showUntil: IsoDateTime })),
    /** Groups linked only to events (hidden outside their windows). */
    eventOnlyGroupIds: z.array(Uuid),
});
export const EventsMgmtTenantSettings = z.object({
    /** Event groups show on the kiosk this many minutes before the start. */
    showBeforeMinutes: z.number().int().min(0).max(240).default(60),
    /** Expected attendees not arrived by the end become no-shows. */
    markNoShowsAtEnd: z.boolean().default(true),
});
//# sourceMappingURL=events-mgmt.js.map